import { AbstractService } from '@/services/core/serviceFactory';

export interface TimelogResponse {
  success: boolean;
  timeLogId?: number;
  message?: string;
}

export class PatientTimelogService extends AbstractService {
  /**
   * Start a new patient timelog session
   */
  async startPatientTimelog(userId: number, patientId: number): Promise<TimelogResponse> {
    this.requireAuth();

    if (!userId || !patientId) {
      throw new Error('User ID and Patient ID are required');
    }

    try {
      // Use apiClient for REST API calls following the three-layer architecture pattern
      const response = await this.apiClient.post(`/api/pep/${userId}/patientTimelog/${patientId}`, {
        duration: 0,
        status: "inprogress"
      }, this.context.token);

      if (response.data && response.data.success && response.data.timeLogId) {
        return {
          success: true,
          timeLogId: response.data.timeLogId
        };
      } else {
        return {
          success: false,
          message: response.data?.message || 'Failed to start timelog'
        };
      }
    } catch (error) {
      throw new Error('Failed to start patient timelog');
    }
  }

  /**
   * Complete a patient timelog session
   */
  async completePatientTimelog(
    userId: number,
    patientId: number,
    timeLogId: number,
    finalDuration: number
  ): Promise<boolean> {
    this.requireAuth();

    if (!userId || !patientId || !timeLogId) {
      throw new Error('User ID, Patient ID, and TimeLog ID are required');
    }

    try {
      // Use apiClient for REST API calls following the three-layer architecture pattern
      // Based on pepApi pattern, complete should be a POST to the same endpoint with "completed" status
      const response = await this.apiClient.post(`/api/pep/${userId}/patientTimelog/${patientId}/${timeLogId}`, {
        duration: finalDuration,
        status: "completed"
      }, this.context.token);

      return response.data?.success || true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Update an existing patient timelog session
   */
  async updatePatientTimelog(
    userId: number,
    patientId: number,
    timeLogId: number,
    duration: number
  ): Promise<boolean> {
    this.requireAuth();

    if (!userId || !patientId || !timeLogId) {
      throw new Error('User ID, Patient ID, and TimeLog ID are required');
    }

    try {
      // Use apiClient for REST API calls following the three-layer architecture pattern
      const response = await this.apiClient.post(`/api/pep/${userId}/patientTimelog/${patientId}/${timeLogId}`, {
        duration,
        status: "inprogress"
      }, this.context.token);

      return response.data?.success || true;
    } catch (error) {
      return false;
    }
  }
}